import { Tooltip } from "@mui/material";
import React from "react";
import { toast } from "react-toastify";

import type {
	TMessage,
	TPaulInputSubmission,
} from "../../types/agent";

import { CopyLinkIcon } from "../../assets/icons/CopyLinkIcon";
import { ForkConvIcon } from "../../assets/icons/fornConv";
import { PencilIcon } from "../../assets/icons/pencilIcon";
import { ReportIcon } from "../../assets/icons/ReportIcon";
import PaulLogo from "../../assets/svg/agent-paul.svg";
import {
	submitFeedback,
	forkConversation,
	getTurnInfo,
} from "../../redux/agent/agent.thunk";
import {
	useAppDispatch,
	useAppSelector,
} from "../../redux/store";
import { services } from "../../services";
import { LoaderWithText } from "../../shared/Loader";
import { isPaulInputResponseMessage } from "../../utils/paulInput";
import { toTenantPath } from "../../utils/tenantUtils";

import EditCodeModal from "./modals/EditCodeModal";
import FeedbackModal from "./modals/FeedbackModal";
import PaulMessageContent from "./PaulMessageContent";
import PaulTurnLoadErrorCard from "./PaulTurnLoadErrorCard";
import CodeRenderer from "./renderers/CodeRenderer";
import ErrorRenderer from "./renderers/ErrorRenderer";
import LargeParagraphRenderer from "./renderers/LargeParagraphRenderer";
import MarkdownRenderer from "./renderers/MarkdownRenderer";

type TMessageProps = {
	message: TMessage;
	isLastAIMessageWithCode?: boolean;
	readOnly?: boolean;
	pendingInputQuestionIds?: Set<string>;
	inputAnswersByQuestionId?: Map<string, string>;
	inputsDisabled?: boolean;
	onSubmitInput?: (
		submission: TPaulInputSubmission
	) => Promise<void> | void;
};

const FEEDBACK_EXPECTED_PREFIX = "Expected: ";
const FEEDBACK_ACTUAL_DELIMITER = ", Actual: ";

const parseFeedbackReason = (reason?: string | null) => {
	if (!reason) {
		return {
			expectedResult: "",
			actualResult: "",
		};
	}

	if (!reason.startsWith(FEEDBACK_EXPECTED_PREFIX)) {
		return {
			expectedResult: "",
			actualResult: reason,
		};
	}

	const actualDelimiterIndex = reason.indexOf(
		FEEDBACK_ACTUAL_DELIMITER,
		FEEDBACK_EXPECTED_PREFIX.length
	);

	if (actualDelimiterIndex === -1) {
		return {
			expectedResult: "",
			actualResult: reason,
		};
	}

	return {
		expectedResult: reason.slice(
			FEEDBACK_EXPECTED_PREFIX.length,
			actualDelimiterIndex
		),
		actualResult: reason.slice(
			actualDelimiterIndex +
				FEEDBACK_ACTUAL_DELIMITER.length
		),
	};
};

const Message: React.FC<TMessageProps> = ({
	message,
	isLastAIMessageWithCode,
	readOnly = false,
	pendingInputQuestionIds,
	inputAnswersByQuestionId,
	inputsDisabled = false,
	onSubmitInput,
}) => {
	const isUser = message.role === "user";
	const dispatch = useAppDispatch();
	const selectedConversation = useAppSelector(
		(state) => state.agent.selectedConversation
	);
	const turnFeedback = useAppSelector(
		(state) =>
			state.agent.turnFeedbackByTurn[message.turn_idx] ??
			null
	);

	const isOwner = selectedConversation?.is_owner ?? true;

	const [loadingFeedback, setLoadingFeedback] =
		React.useState<boolean | null>(null);
	const [feedbackError, setFeedbackError] =
		React.useState<boolean>(false);
	const [isFeedbackModalOpen, setIsFeedbackModalOpen] =
		React.useState(false);
	const [loadingFork, setLoadingFork] =
		React.useState<boolean>(false);

	const [isEditModalOpen, setIsEditModalOpen] =
		React.useState(false);
	const [editLoading, setEditLoading] =
		React.useState(false);
	const [editError, setEditError] = React.useState<
		string | null
	>(null);
	const isTurnLoadError = message.turn_load_error === true;
	const {
		expectedResult: initialExpectedResult,
		actualResult: initialActualResult,
	} = parseFeedbackReason(turnFeedback?.comments);
	const hasFeedback = Boolean(turnFeedback?.comments);

	const getMessageLink = () => {
		if (!selectedConversation) {
			return "";
		}

		return `${window.location.origin}${toTenantPath(
			`/agent/chat/${selectedConversation.conv_id}?messageId=${message.msg_id}`
		)}`;
	};

	const copyMessageLinkToClipboard = () => {
		const messageLink = getMessageLink();
		if (messageLink) {
			void navigator.clipboard.writeText(messageLink);
			toast.success("Message link copied to clipboard");
		}
	};

	const handleReport = () => {
		setFeedbackError(false);
		setIsFeedbackModalOpen(true);
	};

	const handleFeedbackSubmit = async ({
		expectedResult,
		actualResult,
	}: {
		expectedResult: string;
		actualResult: string;
	}) => {
		if (selectedConversation) {
			setLoadingFeedback(false); // actually passing false for downvote/report
			setFeedbackError(false);
			try {
				await dispatch(
					submitFeedback({
						convId: selectedConversation.conv_id,
						turnIdx: message.turn_idx,
						expectedResult,
						actualResult,
						msgLink: getMessageLink(),
					})
				).unwrap();
				toast.success(
					hasFeedback
						? "Report updated"
						: "Report submitted"
				);
				setLoadingFeedback(null);
				setIsFeedbackModalOpen(false);
			} catch (error) {
				setLoadingFeedback(null);
				setFeedbackError(true);
				window.setTimeout(() => {
					setFeedbackError(false);
				}, 3000);
			}
		}
	};

	const handleFork = async () => {
		if (selectedConversation) {
			setLoadingFork(true);
			try {
				const res = await dispatch(
					forkConversation({
						sourceConvId: selectedConversation.conv_id,
						turnIdx: message.turn_idx,
					})
				).unwrap();
				if (res.forked_conv_id) {
					window.location.href = toTenantPath(
						`/agent/chat/${res.forked_conv_id}`
					);
				}
			} catch (error) {
				console.error("Failed to fork conversation", error);
				setLoadingFork(false);
			}
		}
	};

	const handleCodeEdit = async (newCode: string) => {
		if (!selectedConversation) return;

		setEditLoading(true);
		setEditError(null);
		try {
			// Wait for rerun to complete
			await services.agentServiceInstance.rerunMessageCode(
				selectedConversation.conv_id,
				message.turn_idx,
				newCode
			);
			// Reload the affected turn to get the updated attempt data
			await dispatch(
				getTurnInfo({
					conversationId: selectedConversation.conv_id,
					turnIdx: message.turn_idx,
				})
			).unwrap();
			// Close modal only after conversation is reloaded
			setIsEditModalOpen(false);
		} catch (error: unknown) {
			const errorMsg =
				typeof error === "object" &&
				error !== null &&
				typeof (error as { data?: { msg?: unknown } }).data
					?.msg === "string"
					? (error as { data: { msg: string } }).data.msg
					: "Failed to re-run code. Please try again.";
			setEditError(errorMsg);
		} finally {
			setEditLoading(false);
		}
	};

	const handleRetryTurnLoad = async () => {
		if (!selectedConversation || message.turn_idx < 0) {
			throw new Error("This turn cannot be retried.");
		}

		await dispatch(
			getTurnInfo({
				conversationId: selectedConversation.conv_id,
				turnIdx: message.turn_idx,
			})
		).unwrap();
	};

	const contentParts = [...message.content];

	const renderContent = () => {
		return (
			<PaulMessageContent
				parts={contentParts}
				pendingInputQuestionIds={pendingInputQuestionIds}
				inputAnswersByQuestionId={inputAnswersByQuestionId}
				inputsDisabled={inputsDisabled}
				readOnly={readOnly}
				onSubmitInput={onSubmitInput}
			/>
		);
	};

	const renderRawContent = () => {
		if (
			!message.raw_content ||
			Object.keys(message.raw_content).length === 0
		)
			return null;

		return <CodeRenderer content={message.raw_content} />;
	};

	const renderError = () => {
		if (!message.error_msg) return null;
		return (
			<ErrorRenderer
				summary={message.error_msg}
				blobLink={message.error_blob_link}
			/>
		);
	};

	if (isTurnLoadError) {
		return (
			<div
				className="message-container model message-container--load-error"
				id={message.msg_id}
			>
				<img
					src={PaulLogo}
					alt="Paul"
					className="message-avatar no-print"
				/>
				<div className="message-content">
					<PaulTurnLoadErrorCard
						turnIdx={message.turn_idx}
						error={message.error_msg}
						onRetry={handleRetryTurnLoad}
					/>
				</div>
			</div>
		);
	}

	// An input-answer marker renders on its picker (see PaulInputPrompt), never as a user bubble.
	if (isPaulInputResponseMessage(message)) {
		return null;
	}

	if (isUser) {
		if (contentParts[0].msg_type === "large_paragraph") {
			return (
				<div
					className="message-container user"
					id={message.msg_id}
				>
					<div className="message-content">
						<LargeParagraphRenderer
							blobLink={contentParts[0].blob_link}
							description={contentParts[0].description}
							content={contentParts[0].content} // Pass content as fallback
						/>
					</div>
					<span className="message-timestamp">
						{new Date(message.time_millis).toLocaleString(
							undefined,
							{
								month: "short",
								day: "numeric",
								year: "numeric",
								hour: "numeric",
								minute: "numeric",
							}
						)}
					</span>
				</div>
			);
		} else {
			return (
				<div
					className="message-container user"
					id={message.msg_id}
				>
					<div className="message-content">
						<MarkdownRenderer
							content={contentParts[0].content}
						/>
					</div>
					<span className="message-timestamp">
						{new Date(message.time_millis).toLocaleString(
							undefined,
							{
								month: "short",
								day: "numeric",
								year: "numeric",
								hour: "numeric",
								minute: "numeric",
							}
						)}
					</span>
				</div>
			);
		}
	}

	return (
		<div
			className="message-container model"
			id={message.msg_id}
		>
			<img
				src={PaulLogo}
				alt="Paul"
				className="message-avatar no-print"
			/>
			<div className="message-content">
				<div className="model-content-wrapper">
					{renderContent()}
					{renderError()}
					{renderRawContent()}
				</div>
				<div className="message-footer">
					<span className="message-timestamp">
						{new Date(message.time_millis).toLocaleString(
							undefined,
							{
								month: "short",
								day: "numeric",
								year: "numeric",
								hour: "numeric",
								minute: "numeric",
							}
						)}
					</span>
					{!readOnly && (
						<div className="feedback-buttons">
							{feedbackError && (
								<Tooltip title="Failed to submit feedback">
									<span className="feedback-error">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 24 24"
											fill="currentColor"
											className="error-icon"
										>
											<path
												fillRule="evenodd"
												d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
												clipRule="evenodd"
											/>
										</svg>
									</span>
								</Tooltip>
							)}
							{isOwner && isLastAIMessageWithCode && (
								<Tooltip title="Edit and Re-run Code">
									<button
										className="feedback-btn"
										onClick={() => {
											setIsEditModalOpen(true);
										}}
									>
										<PencilIcon />
									</button>
								</Tooltip>
							)}
							{isOwner && (
								<Tooltip
									title={
										hasFeedback ? "Edit Report" : "Report"
									}
								>
									<button
										className={`feedback-btn ${
											hasFeedback ? "active" : ""
										}`}
										onClick={handleReport}
										disabled={loadingFeedback !== null}
									>
										<ReportIcon />
									</button>
								</Tooltip>
							)}
							<Tooltip title="Fork Conversation">
								<button
									className="feedback-btn"
									onClick={handleFork}
									disabled={loadingFork}
								>
									{loadingFork ? (
										<LoaderWithText />
									) : (
										<ForkConvIcon />
									)}
								</button>
							</Tooltip>
							<Tooltip title="Copy Link">
								<button
									className="feedback-btn"
									onClick={copyMessageLinkToClipboard}
								>
									<CopyLinkIcon />
								</button>
							</Tooltip>
						</div>
					)}
				</div>
			</div>
			<FeedbackModal
				open={isFeedbackModalOpen}
				onClose={() => {
					setFeedbackError(false);
					setIsFeedbackModalOpen(false);
				}}
				onSubmit={handleFeedbackSubmit}
				loading={loadingFeedback !== null}
				error={feedbackError}
				initialExpectedResult={initialExpectedResult}
				initialActualResult={initialActualResult}
			/>
			<EditCodeModal
				open={isEditModalOpen}
				onClose={() => {
					setIsEditModalOpen(false);
				}}
				code={message.raw_content ?? null}
				onSave={handleCodeEdit}
				loading={editLoading}
				error={editError}
			/>
		</div>
	);
};

export default Message;
